import { type JSX, ErrorBoundary as SolidErrorBoundary } from "solid-js";
import { RefreshCw, RotateCcw } from "@fleet/icons";

import { Button } from "@/components/ui/button";
import { GlassSurface } from "@fleet/ui";
import { BrokenClipboardIllustration } from "@/components/ui/illustrations";
import { useI18n } from "@/providers/i18n-provider";
import { msg } from "@lingui/core/macro";
import { cn } from "@custodia/shared";

interface ErrorFallbackProps {
  error: unknown;
  reset: () => void;
  /** When false, renders a compact inline card instead of a full-screen panel. */
  fullScreen?: boolean;
  /** Optional region label shown in the title (e.g. "the dashboard"). */
  region?: string;
}

/**
 * Premium, theme-aware fallback for caught render errors. Shows a theme-safe SVG
 * illustration, a clear title/description, and two recovery affordances:
 *  - "Retry": re-runs the failed boundary subtree (Solid `reset`).
 *  - "Reload": hard-reloads the page as a last resort.
 * Never a blank screen.
 */
function ErrorFallback(props: ErrorFallbackProps) {
  const { t } = useI18n();

  const message = () =>
    props.error instanceof Error ? props.error.message : t(msg`An unexpected error has occurred.`);

  const title = () =>
    props.region ? t(msg`This section could not load`) : t(msg`Something went wrong`);

  return (
    <div
      role="alert"
      class={cn(
        "flex w-full items-center justify-center p-4",
        props.fullScreen ? "min-h-dvh" : "flex-1 py-10",
      )}
    >
      <GlassSurface
        class={cn(
          "flex w-full flex-col items-center text-center",
          props.fullScreen ? "max-w-md p-8 space-y-6" : "max-w-sm p-6 space-y-5",
        )}
      >
        <div class="text-danger/70 animate-scale-in">
          <BrokenClipboardIllustration class={props.fullScreen ? "h-32 w-32" : "h-24 w-24"} />
        </div>
        <div class="space-y-2">
          <h2 class={cn("font-bold tracking-tight", props.fullScreen ? "text-2xl" : "text-lg")}>
            {title()}
          </h2>
          <p class="mx-auto max-w-xs text-sm leading-relaxed text-muted-foreground">
            {props.region
              ? t(msg`We hit a snag rendering this part of the app. Your data is safe — try again.`)
              : t(msg`We hit an unexpected snag. Your work is saved locally — try again.`)}
          </p>
          <p class="mx-auto max-w-xs truncate text-xs text-muted-foreground/70" title={message()}>
            {message()}
          </p>
        </div>
        <div class="flex w-full gap-3">
          <Button
            variant="outline"
            class="flex-1 gap-2"
            onClick={() => {
              globalThis.location.reload();
            }}
          >
            <RefreshCw class="h-4 w-4" />
            {t(msg`Reload`)}
          </Button>
          <Button
            class="flex-1 gap-2"
            onClick={() => {
              props.reset();
            }}
          >
            <RotateCcw class="h-4 w-4" />
            {t(msg`Retry`)}
          </Button>
        </div>
      </GlassSurface>
    </div>
  );
}

interface AppErrorBoundaryProps {
  children: JSX.Element;
  /** Full-screen panel (root) vs. inline region card. Defaults to inline. */
  fullScreen?: boolean;
  /** Human-readable region name; presence switches copy to the region variant. */
  region?: string;
}

/**
 * Solid `<ErrorBoundary>` wrapper with our premium fallback and centralized
 * logging. Use one at the root (fullScreen) and one around each data region so a
 * thrown render error degrades to a recoverable card, never a white screen.
 */
export function AppErrorBoundary(props: AppErrorBoundaryProps) {
  // Centralized logging — surfaced for diagnostics; never swallowed silently.
  const logError = (error: unknown) => {
    console.error(
      `[AppErrorBoundary${props.region ? `:${props.region}` : ""}] Caught render error:`,
      error,
    );
  };

  // The fallback render-prop returns an <ErrorFallback> element. It is a thin
  // pass-through (not a component definition) so the boundary owns recovery.
  const renderFallback = (error: unknown, reset: () => void): JSX.Element => {
    logError(error);
    return (
      <ErrorFallback
        error={error}
        reset={reset}
        fullScreen={props.fullScreen}
        region={props.region}
      />
    );
  };

  return <SolidErrorBoundary fallback={renderFallback}>{props.children}</SolidErrorBoundary>;
}
